import { makeAutoObservable, runInAction } from 'mobx';
import { AddEmployeesAction, Config, ConfigProps, Domain, Feature, RenewalAction } from 'types/domainServiceV2';
import apolloClient from 'api/apolloClient';
import { DOMAIN_V2_BY_ID } from 'api/queries/domainServiceV2';
import { getEffectiveDates } from '@insurancemenu/imenu-components-lib/dist/helpers';

export const INITIAL_DOMAIN_OBJECT: Readonly<Domain> = Object.freeze<Domain>({
  id: '',
  name: '',
  logo: '',
  headerStyleId: 'default',
  properties: {
    effectiveDatesAmount: 0,
    retroActiveDaysAmount: 0,
    retroQuotingDaysAmount: 0,
    productsAvailableYear: 0,
    eligibleStates: [],
    ineligibleSicCodes: [],
    availableRenewalActions: [],
    eeClassesRequired: false,
    newProposalAdditionalInputs: {
      required: [],
      optional: [],
    },
    availablePipelineStatuses: [],
    availableFeatures: [],
  },
  dateDependent: [],
});

export const INITIAL_DOMAIN_CONFIGURATION: Readonly<Config> = Object.freeze({
  GROUP: {
    annualPremiumFormId: '',
    censusBasedCost: false,
    resubmitIneligible: false,
  },
});

export const INITIAL_DOMAIN_CONFIG_PROPS: Readonly<ConfigProps> = Object.freeze({
  resubmitIneligible: false,
});

export class DomainStoreV2 {
  domainHeader: Domain = { ...INITIAL_DOMAIN_OBJECT };
  loading = true;

  public async getDomainById(domainId: string): Promise<void> {
    const res = await apolloClient.query<{ getDomainById: Domain }>({
      query: DOMAIN_V2_BY_ID,
      variables: {
        id: domainId,
      },
    });

    runInAction(() => {
      this.domainHeader = res.data.getDomainById;
      this.loading = false;
    });
  }

  public isEligibleState(stateFieldValue: string): boolean {
    return (
      !this.domainHeader.properties.eligibleStates ||
      this.domainHeader.properties.eligibleStates?.includes(stateFieldValue)
    );
  }

  public isEligibleSicCode(sicFieldValue: string): boolean {
    return !this.domainHeader.properties.ineligibleSicCodes?.includes(sicFieldValue);
  }

  public get sponsorId(): string {
    return this.domainHeader.id;
  }

  public get isChangePlanActionAvailable(): boolean {
    return this.domainHeader.properties?.availableRenewalActions?.includes(RenewalAction.CHANGE_PLAN) || false;
  }

  public get isRenewAsIsActionAvailable(): boolean {
    return this.domainHeader.properties?.availableRenewalActions?.includes(RenewalAction.RENEW_AS_IS) || false;
  }

  public get isCancelCoverageActionAvailable(): boolean {
    return this.domainHeader.properties?.availableRenewalActions?.includes(RenewalAction.CANCEL_COVERAGE) || false;
  }

  public get canBrowseInstalledEffectiveDate(): boolean {
    return (
      this.domainHeader.properties?.availableRenewalActions?.includes(RenewalAction.BROWSE_INSTALLED_PERIOD) || false
    );
  }

  public get showCustomAttributesFeature(): boolean {
    return !this.domainHeader?.properties.availableFeatures?.includes(Feature.DISABLE_CUSTOM_ATTRIBUTES);
  }

  public get showInitialSeedFeature(): boolean {
    return this.domainHeader?.properties.availableFeatures?.includes(Feature.INITIAL_SEED) || false;
  }

  public get showQuotingUWGuidelinesFeature(): boolean {
    return this.domainHeader?.properties.availableFeatures?.includes(Feature.SHOW_QUOTING_UW_GUIDELINES) || false;
  }

  public get showSelfEnrollFeature(): boolean {
    return this.domainHeader?.properties.availableFeatures?.includes(Feature.SELF_ENROLL_ENABLED) || false;
  }

  public get showCensusBypassFeature(): boolean {
    return this.domainHeader?.properties.availableFeatures?.includes(Feature.CENSUS_BYPASS) || false;
  }

  public get retroQuotingDaysAmount(): number {
    return this.domainHeader?.properties?.retroQuotingDaysAmount || 0;
  }

  public get availableAddEmployeesActions(): AddEmployeesAction[] {
    return this.domainHeader?.properties.availableAddEmployeesActions || [];
  }

  public get availableClasses(): string[] {
    return this.domainHeader?.properties.availableClasses || [];
  }

  public get allowTinOnlyGroupCreation(): boolean {
    return this.domainHeader?.properties.allowTinOnlyGroupCreation || false;
  }

  public get adminOnlyAgentAssignment(): boolean {
    return this.domainHeader?.properties.adminOnlyAgentAssignment || false;
  }

  public get groupInitiationRedirect(): boolean {
    return this.domainHeader?.properties.groupInitiationRedirect || false;
  }

  public get imqSponsor(): boolean {
    return this.domainHeader?.properties.imqSponsor || false;
  }

  public get quotingEstimatedGroupSize(): boolean {
    return this.domainHeader?.properties.quotingEstimatedGroupSize || false;
  }

  /**
   * List of effective dates.
   * Additional availability rules (beyond domain data) should be applied externally if needed.
   */
  public get effectiveDates(): string[] {
    return getEffectiveDates(
      this.domainHeader.properties.effectiveDatesAmount,
      this.domainHeader.properties.productsAvailableYear,
      this.domainHeader.properties.retroQuotingDaysAmount,
      this.domainHeader.properties.productsAvailableMonth,
    );
  }

  public isRetroQuotingPeriodPassed(effectiveDate: string): boolean {
    if (!effectiveDate || this.retroQuotingDaysAmount === 0) return false;

    const atMidnight = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const effectiveDateMidnight = atMidnight(new Date(effectiveDate));
    const currentDateMidnight = atMidnight(new Date());

    // 86400000 is the number of milliseconds in a day
    const diffDays = (currentDateMidnight.getTime() - effectiveDateMidnight.getTime()) / 86400000;
    return diffDays > this.retroQuotingDaysAmount;
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
